'use client';

import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { Schedule } from '@/types/api';

interface SchedulePickerProps {
  schedules: Schedule[];
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  triggerClassName?: string;
}

// Helper to group and sort schedules
function organizeSchedules(schedules: Schedule[]) {
  const core: Schedule[] = [];
  const modified: Schedule[] = [];
  const special: Schedule[] = [];
  const drafts: Schedule[] = [];

  schedules.forEach((schedule) => {
    if (schedule.isDraft || schedule.isUserDraft) {
      drafts.push(schedule);
    } else if (schedule.variant === 'core') {
      core.push(schedule);
    } else if (schedule.variant === 'modified') {
      modified.push(schedule);
    } else if (schedule.variant === 'special') {
      special.push(schedule);
    }
  });

  // Sort core schedules by order if available, otherwise alphabetically
  core.sort((a, b) => {
    if (a.variant === 'core' && b.variant === 'core') {
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
    }
    return a.displayName.localeCompare(b.displayName);
  });

  // Sort others alphabetically
  modified.sort((a, b) => a.displayName.localeCompare(b.displayName));
  special.sort((a, b) => a.displayName.localeCompare(b.displayName));
  drafts.sort((a, b) => a.displayName.localeCompare(b.displayName));

  return { core, modified, special, drafts };
}

export function SchedulePicker({
  schedules,
  value,
  onValueChange,
  disabled = false,
  placeholder = 'Select schedule...',
  triggerClassName,
}: SchedulePickerProps) {
  const [open, setOpen] = React.useState(false);
  
  const selectedSchedule = schedules.find((s) => s.id === value);
  const { core, modified, special, drafts } = React.useMemo(
    () => organizeSchedules(schedules),
    [schedules]
  );

  const handleSelect = (scheduleId: string) => {
    onValueChange(scheduleId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between', triggerClassName)}
          disabled={disabled}
        >
          <span className="truncate">
            {selectedSchedule?.displayName || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search schedules..." />
          <CommandList>
            <CommandEmpty>No schedule found.</CommandEmpty>
            
            {core.length > 0 && (
              <CommandGroup heading="Core Schedules">
                {core.map((schedule) => (
                  <CommandItem
                    key={schedule.id}
                    value={schedule.displayName}
                    onSelect={() => handleSelect(schedule.id)}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <Check
                        className={cn(
                          'h-4 w-4',
                          value === schedule.id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span>{schedule.displayName}</span>
                    </div>
                    {schedule.variant === 'core' && schedule.dayType && (
                      <span className="text-xs text-muted-foreground">
                        {schedule.dayType.name}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {modified.length > 0 && (
              <CommandGroup heading="Modified Schedules">
                {modified.map((schedule) => (
                  <CommandItem
                    key={schedule.id}
                    value={schedule.displayName}
                    onSelect={() => handleSelect(schedule.id)}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <Check
                        className={cn(
                          'h-4 w-4',
                          value === schedule.id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span>{schedule.displayName}</span>
                    </div>
                    {schedule.variant === 'modified' && schedule.modification && (
                      <span className="text-xs text-muted-foreground">
                        {schedule.modification}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {special.length > 0 && (
              <CommandGroup heading="Special Schedules">
                {special.map((schedule) => (
                  <CommandItem
                    key={schedule.id}
                    value={schedule.displayName}
                    onSelect={() => handleSelect(schedule.id)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === schedule.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {schedule.displayName}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {drafts.length > 0 && (
              <CommandGroup heading="Draft Schedules">
                {drafts.map((schedule) => (
                  <CommandItem
                    key={schedule.id}
                    value={schedule.displayName}
                    onSelect={() => handleSelect(schedule.id)}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <Check
                        className={cn(
                          'h-4 w-4',
                          value === schedule.id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span>{schedule.displayName}</span>
                    </div>
                    <span className="text-xs text-muted-foreground italic">
                      Draft
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

